<template>
  <transition mode="out-in" name="fade">
    <div v-if="slider" :class="classes.transitionWrapper">
      <component :is="slider">
        <CardSelector
          v-for="(card, i) in cards"
          :key="String(i)"
          :cardNumber="String(i + 1)"
          :cardType="card.type"
          :marketIds="card.marketIds"
          :optionIds="card.optionIds"
          :eventId="card.eventId"
        />
      </component>
    </div>
    <div
      v-else-if="cards.length"
      :class="classes.transitionWrapper"
      :style="{ overflow: 'hidden' }"
    >
      <CardSelector
        :cardType="cards[0].type"
        :marketIds="cards[0].marketIds"
        :optionIds="cards[0].optionIds"
      />
    </div>
  </transition>
</template>

<script lang="ts">
import CardSelector from "./CardSelector.vue";
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  defineComponent,
  inject,
  Ref,
  watchEffect
} from "vue";

import { injectBetData } from "@fresh8/ad-data-composition/src/betData";
import { createUseStyles } from "@fresh8/vue-jss/src";

import { injectPreview } from "@fresh8/ad-data-composition/src/preview";

import { provideCardsContext } from "../lib/card-analytics";
import {
  useHandleCardAnalytics,
  createCardsContext,
  injectCardsContext
} from "../lib/card-analytics";
import { AnalyticEvents, CardsContext } from "@fresh8/types/src/analytics";
import { BetData } from "@fresh8/types/src/betData";
import { provideAnimationSync } from "../hooks/useAnimationSync";
import { injectMarketCards } from "../composition/card-creation/index";
import {
  injectTemplateSize,
  TemplateSizeContext,
  LMLB
} from "@fresh8/ad-data-composition/src/useTemplateSize";
import { injectCasinoData, isSuccess } from "../lib/provideInjectors";
import {
  AdTagContext,
  injectAdTag
} from "@fresh8/ad-data-composition/src/brand";
import { CasinoDataSuccess } from "@fresh8/types/src";
import { useAnalytics } from "../hooks/useAnalytics";

const useStyles = createUseStyles({
  transitionWrapper: {
    flex: "1",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box"
  }
});

export default defineComponent({
  name: "Content",
  components: {
    CardSelector
  },
  setup() {
    const classes = useStyles();

    const adTagContext = injectAdTag() || ({} as AdTagContext);
    const betDataContext = injectBetData();
    const previewContext = injectPreview();
    const cardsC = injectCardsContext();

    const { handleCardAnalyticsViewed } = useAnalytics({}, "Impression");

    provideAnimationSync();

    const { cards: marketCards } = injectMarketCards();
    const { size } = injectTemplateSize() as TemplateSizeContext;

    const cards = computed(() => {
      if (size.value.name === LMLB.name) {
        return marketCards.value.slice(0, 1);
      }

      return marketCards.value;
    });

    const casinoContext = injectCasinoData();

    const casinoData = computed(() => {
      return isSuccess(casinoContext.value)
        ? casinoContext.value
        : ({} as CasinoDataSuccess);
    });

    const cardContext = createCardsContext(
      betDataContext?.betData as Ref<BetData>,
      cards,
      casinoData as Ref<CasinoDataSuccess>
    );
    provideCardsContext(cardContext as ComputedRef<CardsContext>);

    const refireAfter = computed(() => {
      const operator = adTagContext.operator.value?._id;
      const teamFeatureFlags =
        adTagContext.teamFeatureFlags.value?.pixelReimpression;

      if (teamFeatureFlags && operator) {
        const refireAfter =
          adTagContext.adTag.value?.tracking[operator].refireAfter || 0;

        return refireAfter;
      }

      return 0;
    });

    handleCardAnalyticsViewed(
      cardContext.value?.cards || [],
      refireAfter.value
    );

    const slider = computed(() => {
      if ((cards.value?.length || 0) > 1) {
        return defineAsyncComponent(() => import("./Carousel"));
      }

      return false;
    });

    const currentEventId = inject("currentEvent", {
      setCurrentEventId: (value: string) => void 0
    });

    watchEffect(() => {
      if (cards.value?.length === 1) {
        currentEventId.setCurrentEventId(cards.value[0]?.eventId);
      }
    });

    // If no markets and no offer then collapse the ad
    watchEffect(() => {
      if (
        (cards.value?.length || 0) === 0 &&
        !previewContext?.preview.value.isPreview
      ) {
        window.parent.postMessage({ type: "f8_failed_loading" }, "*");
        document.documentElement.style.height = "0px";
        document.documentElement.style.overflow = "hidden";
        document.body.style.height = "0px";
        document.body.style.overflow = "hidden";
      } else if (document.documentElement.style.overflow === "hidden") {
        document.documentElement.style.height = "";
        document.documentElement.style.overflow = "";
        document.body.style.height = "";
        document.body.style.overflow = "";
      }

      const newCardContext = createCardsContext(
        betDataContext?.betData as Ref<BetData>,
        cards,
        casinoData as Ref<CasinoDataSuccess>
      );

      cardsC.value.cards = newCardContext?.value?.cards || [];
    });

    return {
      classes,
      slider,
      cards
    };
  }
});
</script>
